import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { PriceDisplay, PriceButton } from '@/components/PriceDisplay';
import { getDisplayPrice } from '@/lib/priceUtils';
import { durationColor, difficultyColor } from '@/lib/badgeColors';
import { Zap, Clock, CheckCircle, ArrowLeft, Play, Pause, ShoppingCart, Star, Sun, Moon, FileText, Volume2, Download, ExternalLink, Headphones, Package } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function StorefrontProduct() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [product, setProduct] = useState(null);
  const [offers, setOffers] = useState([]);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [playingTeaser, setPlayingTeaser] = useState(false);
  const [includedGuides, setIncludedGuides] = useState([]);
  const [playingGuideTeaser, setPlayingGuideTeaser] = useState(null);
  const teaserAudioRef = React.useRef(null);
  const guideTeaserRef = React.useRef(null);

  useEffect(() => {
    api.get(`/public/products/slug/${slug}`).then(r => {
      setProduct(r.data);
      // Meta Pixel: ViewContent
      if (window.fbq) window.fbq('track', 'ViewContent', { content_name: r.data.title, content_ids: [r.data.id], content_type: 'product', value: r.data.discounted_price ?? r.data.price, currency: 'USD' });
      api.get(`/public/offers/product/${r.data.id}`).then(or => setOffers(or.data)).catch(() => {});
      // Fetch included guides for packages
      if (r.data.type === 'package' && r.data.included_guides?.length > 0) {
        api.get('/public/products').then(pr => {
          const guides = r.data.included_guides
            .map(gid => pr.data.find(p => p.id === gid))
            .filter(Boolean);
          setIncludedGuides(guides);
        }).catch(() => {});
      }
    }).catch(() => navigate('/'));
    api.post('/analytics/event', { event_type: 'page_view', product_id: '', page_id: '', metadata: { slug } }).catch(() => {});
  }, [slug, navigate]);

  if (!product) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#1A9DA5] border-t-transparent rounded-full" /></div>;

  const handleCheckout = () => {
    const params = new URLSearchParams({ product_id: product.id });
    if (selectedOffers.length) params.set('offers', selectedOffers.join(','));
    navigate(`/checkout?${params.toString()}`);
  };

  const toggleOffer = (id) => {
    setSelectedOffers(prev => prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]);
  };

  const playGuideTeaser = (guide) => {
    if (!guide.teaser_audio_base64) return;
    if (playingGuideTeaser === guide.id) {
      guideTeaserRef.current?.pause();
      setPlayingGuideTeaser(null);
      return;
    }
    // Stop main teaser if playing
    if (playingTeaser) { teaserAudioRef.current?.pause(); setPlayingTeaser(false); }
    if (guideTeaserRef.current) {
      guideTeaserRef.current.src = `data:audio/mp3;base64,${guide.teaser_audio_base64}`;
      guideTeaserRef.current.play();
      setPlayingGuideTeaser(guide.id);
    }
  };

  return (
    <div className="min-h-screen" data-testid="product-page">
      <header className="sticky top-0 z-50 border-b glass-effect bg-background/80">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Wayfinder" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-lg font-['Outfit']">Wayfinder</span>
          </Link>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
        </div>
      </header>

      <main>
        {/* PRODUCT HERO BANNER */}
        <div className="relative overflow-hidden" data-testid="product-hero">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0C2D3F] via-[#0F3D4D] to-[#1A4A5A]" />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 30% 40%, rgba(26,157,165,0.3) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(232,115,90,0.15) 0%, transparent 40%)' }} />
          <div className="absolute top-10 right-[15%] w-48 h-48 rounded-full bg-[#1A9DA5]/10 blur-3xl" />
          <div className="absolute bottom-0 left-[10%] w-32 h-32 rounded-full bg-[#E8735A]/10 blur-3xl" />
          {/* Decorative elements */}
          <div className="absolute top-8 left-[8%] grid grid-cols-4 gap-1.5 opacity-15">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="w-1 h-1 rounded-full bg-[#F0B429]" />)}
          </div>
          <div className="absolute bottom-12 right-[8%] w-20 h-20 rounded-full border border-white/5" />
          <div className="absolute bottom-12 right-[8%] w-14 h-14 rounded-full border border-white/5 translate-x-1 translate-y-1" />

          <div className="relative max-w-5xl mx-auto px-4 pt-6 pb-12">
            <Button variant="ghost" className="mb-6 text-white/60 hover:text-white hover:bg-white/10" onClick={() => navigate('/')} data-testid="back-to-store-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />Back to Store
            </Button>

            <div className="grid md:grid-cols-5 gap-8 items-start">
              <div className="md:col-span-3 text-white">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Badge className="bg-white/15 text-white border-white/20 backdrop-blur">{product.category}</Badge>
                  <Badge className="capitalize bg-white/15 text-white border-white/20 backdrop-blur">{product.difficulty}</Badge>
                  <Badge className="bg-white/15 text-white border-white/20 backdrop-blur"><Clock className="w-3 h-3 mr-1" />{product.duration_minutes} min</Badge>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-3 leading-tight">{product.title}</h1>
                <p className="text-lg text-white/60 mb-6">{product.subtitle}</p>

                {product.teaser_audio_base64 && (
                  <div className="mb-4">
                    <audio ref={teaserAudioRef} onEnded={() => setPlayingTeaser(false)} />
        <audio ref={guideTeaserRef} onEnded={() => setPlayingGuideTeaser(null)} />
                    <Button
                      size="sm"
                      onClick={() => {
                        if (playingTeaser) { teaserAudioRef.current?.pause(); setPlayingTeaser(false); }
                        else if (teaserAudioRef.current) {
                          teaserAudioRef.current.src = `data:audio/mp3;base64,${product.teaser_audio_base64}`;
                          teaserAudioRef.current.play();
                          setPlayingTeaser(true);
                        }
                      }}
                      className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur gap-2"
                      data-testid="play-teaser-audio-btn"
                    >
                      {playingTeaser ? <Pause className="w-4 h-4 text-[#1A9DA5]" /> : <Volume2 className="w-4 h-4 text-[#1A9DA5]" />}
                      {playingTeaser ? 'Pause Preview' : 'Listen to Preview'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Price card */}
              <div className="md:col-span-2">
                <Card className="border-2 border-white/10 bg-white/5 backdrop-blur shadow-2xl shadow-black/20" data-testid="product-price-card">
                  <CardContent className="p-6 text-center">
                    {(() => {
                      const { hasDiscount, isFree, discounted, original } = getDisplayPrice(product);
                      if (isFree) return (<>
                        <p className="text-3xl font-bold text-[#1A9DA5] font-['Outfit'] mb-0.5">Free!</p>
                        {original > 0 && <p className="text-white/40 line-through text-sm">${original}</p>}
                        <p className="text-white/50 text-sm mb-4">{original > 0 ? 'limited time' : 'Free access'}</p>
                      </>);
                      if (hasDiscount) return (<>
                        <p className="text-3xl font-bold text-[#E8735A] font-['Outfit'] mb-0.5">${discounted.toFixed(2)}</p>
                        <p className="text-white/40 line-through text-sm">${original}</p>
                        <p className="text-white/50 text-sm mb-4">one-time payment</p>
                      </>);
                      return (<>
                        <p className="text-4xl font-bold text-white font-['Outfit'] mb-1">${original}</p>
                        <p className="text-white/50 text-sm mb-4">one-time payment</p>
                      </>);
                    })()}
                    <Button
                      size="lg"
                      className={`w-full ${getDisplayPrice(product).isFree ? 'bg-[#1A9DA5]' : 'bg-[#1A9DA5]'} hover:bg-[#158D92] text-white shadow-lg shadow-[#1A9DA5]/25 transition-all hover:-translate-y-0.5`}
                      onClick={handleCheckout}
                      data-testid="product-buy-btn"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" /><PriceButton product={product} />
                    </Button>
                    <p className="text-white/40 text-xs mt-3">{getDisplayPrice(product).isFree ? 'Enter your email and get instant access. No payment needed, no spam.' : 'Instant access after purchase'}</p>
                    {product.pdf_asset_id && (
                      <div className="flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-white/10">
                        <FileText className="w-3.5 h-3.5 text-[#E8735A]" />
                        <span className="text-xs text-white/60">PDF document included</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT */}
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
              {product.short_description && (
                <p className="text-base leading-relaxed">{product.short_description}</p>
              )}

              {/* Included Guides (for packages) */}
              {product.type === 'package' && includedGuides.length > 0 && (
                <div data-testid="included-guides-section">
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="w-5 h-5 text-[#1A9DA5]" />
                    <h2 className="text-xl font-semibold font-['Outfit']">What's Inside This Package</h2>
                    <Badge variant="outline" className="ml-auto">{includedGuides.length} guides</Badge>
                  </div>
                  <div className="space-y-3">
                    {includedGuides.map(g => (
                      <Card key={g.id} className="hover:border-[#1A9DA5]/30 transition-colors" data-testid={`included-guide-${g.id}`}>
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            <div className="w-11 h-11 rounded-xl bg-[#1A9DA5]/10 flex items-center justify-center shrink-0 mt-0.5">
                              <Headphones className="w-5 h-5 text-[#1A9DA5]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-semibold text-base font-['Outfit']">{g.title}</h3>
                                {g.duration_minutes && (
                                  <Badge variant="outline" className={`text-xs ${durationColor(g.duration_minutes).bg} ${durationColor(g.duration_minutes).text} ${durationColor(g.duration_minutes).border}`}>
                                    <Clock className="w-3 h-3 mr-0.5" />{g.duration_minutes} min
                                  </Badge>
                                )}
                                {g.difficulty && <Badge variant="outline" className="text-xs capitalize">{g.difficulty}</Badge>}
                              </div>
                              {g.short_description && (
                                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{g.short_description}</p>
                              )}
                              <div className="flex items-center gap-3 flex-wrap">
                                <PriceDisplay product={g} size="sm" />
                                {g.category && <Badge variant="outline" className="text-xs">{g.category}</Badge>}
                                {g.teaser_audio_base64 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs gap-1.5"
                                    onClick={() => playGuideTeaser(g)}
                                    data-testid={`play-guide-teaser-${g.id}`}
                                  >
                                    {playingGuideTeaser === g.id ? <Pause className="w-3 h-3 text-[#1A9DA5]" /> : <Volume2 className="w-3 h-3 text-[#1A9DA5]" />}
                                    {playingGuideTeaser === g.id ? 'Pause' : 'Preview'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {product.outcomes?.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold font-['Outfit'] mb-3">What You'll Get</h2>
                  <div className="space-y-2">
                    {product.outcomes.map((o, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-[#1A9DA5] shrink-0 mt-0.5" />
                        <span className="text-sm">{o}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.teaser_script && (
                <Card className="border-l-4 border-l-[#1A9DA5]">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold font-['Outfit'] mb-3 flex items-center gap-2"><Play className="w-5 h-5 text-[#1A9DA5]" />Preview</h3>
                    {product.teaser_audio_base64 && (
                      <div className="mb-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (playingTeaser) { teaserAudioRef.current?.pause(); setPlayingTeaser(false); }
                            else if (teaserAudioRef.current) {
                              teaserAudioRef.current.src = `data:audio/mp3;base64,${product.teaser_audio_base64}`;
                              teaserAudioRef.current.play();
                              setPlayingTeaser(true);
                            }
                          }}
                          className="gap-2"
                          data-testid="play-teaser-content-btn"
                        >
                          {playingTeaser ? <Pause className="w-4 h-4 text-[#1A9DA5]" /> : <Volume2 className="w-4 h-4 text-[#1A9DA5]" />}
                          {playingTeaser ? 'Pause Preview' : 'Listen to Preview'}
                        </Button>
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground italic">{product.teaser_script}</p>
                  </CardContent>
                </Card>
              )}

            {product.pdf_asset_id && (
              <Card className="border-l-4 border-l-blue-500" data-testid="product-pdf-card">
                <CardContent className="p-6 flex items-center gap-4">
                  <FileText className="w-10 h-10 text-red-500 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-base font-semibold font-['Outfit']">Downloadable PDF Included</h3>
                    <p className="text-sm text-muted-foreground">{product.pdf_filename || 'Guide document'} — included with purchase</p>
                  </div>
                  <Download className="w-5 h-5 text-muted-foreground" />
                </CardContent>
              </Card>
            )}

            {product.external_link_url && (
              <Card className="border-l-4 border-l-[#1A9DA5]" data-testid="product-external-link-card">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1A9DA5]/10 flex items-center justify-center shrink-0">
                    <ExternalLink className="w-5 h-5 text-[#1A9DA5]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold font-['Outfit']">{product.external_link_label || 'Recommended Resource'}</h3>
                    <p className="text-sm text-muted-foreground">Companion tool for this guide</p>
                  </div>
                  <a href={product.external_link_url} target="_blank" rel="noopener noreferrer" data-testid="product-external-link-btn">
                    <Button size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">
                      <ExternalLink className="w-4 h-4 mr-1" />{product.external_link_label || 'Recommended Resource'}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            )}

            {product.long_description && (
              <div>
                <h2 className="text-xl font-semibold font-['Outfit'] mb-3">About This Guide</h2>
                <p className="text-sm leading-relaxed text-muted-foreground">{product.long_description}</p>
              </div>
            )}

            {product.testimonials?.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold font-['Outfit'] mb-3">What Others Say</h2>
                <div className="space-y-3">
                  {product.testimonials.map((t, i) => (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <div className="flex gap-1 mb-2">{[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-[#1A9DA5] text-[#1A9DA5]" />)}</div>
                        <p className="text-sm italic mb-2">"{t.quote}"</p>
                        <p className="text-xs font-medium">{t.name} <span className="text-muted-foreground">- {t.business}</span></p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {product.faq?.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold font-['Outfit'] mb-3">FAQ</h2>
                <Accordion type="single" collapsible>
                  {product.faq.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-sm text-left">{f.question}</AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground">{f.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          <div className="md:col-span-1">
            <div className="sticky top-24 space-y-4">
              <Card className="border-2 border-[#1A9DA5]/30">
                <CardContent className="p-5 space-y-3">
                  <div className="text-center">
                    <PriceDisplay product={product} size="md" />
                    {product.compare_at_price && !getDisplayPrice(product).hasDiscount && <span className="text-sm text-muted-foreground line-through ml-2">${product.compare_at_price}</span>}
                    {product.savings_display && <Badge className="bg-[#F0B429] text-white ml-2">{product.savings_display}</Badge>}
                  </div>
                  <Button data-testid="buy-now-btn" onClick={handleCheckout} className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white py-5 font-semibold">
                    <ShoppingCart className="w-4 h-4 mr-2" /><PriceButton product={product} />
                  </Button>

                  {offers.filter(o => o.type === 'order_bump').map(offer => (
                    <div key={offer.id} className="p-3 rounded-lg border border-dashed border-[#1A9DA5]/30 bg-[#1A9DA5]/5">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" checked={selectedOffers.includes(offer.id)} onChange={() => toggleOffer(offer.id)} className="mt-1 accent-[#1A9DA5]" />
                        <div>
                          <p className="text-sm font-medium">{offer.headline || offer.name}</p>
                          <p className="text-xs text-muted-foreground">+${offer.price}</p>
                        </div>
                      </label>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}
